import type { Unsubscribe } from 'firebase/firestore';

/**
 * Module-level Firestore listener registry(R35 / Security panel red line)。
 *
 * 集中管理 `onSnapshot` unsubscribe handles,讓 `onAuthLost` / logout 流程可以一次
 * detach 全部 active listener。對應 ADR-002 onAuthLost 順序固定:
 *
 *   1. detach all listener  ← 最先,避免用過期 token 噴 permission-denied toast
 *   2. firebaseAuth.signOut()
 *   3. useAuthStore.clear() + clearActiveJobs() + clearProjectAssets()
 *   4. navigate('/login')
 *
 * Caller 透過 `useFirestoreSubscription` 集中 hook 走進來,不應直接 import register。
 * ESLint rule `hai/no-raw-onsnapshot` 強制 `onSnapshot` 走 hook,避免漏 register。
 */

const listeners = new Set<Unsubscribe>();

/**
 * 註冊一個 `Unsubscribe` handle。`useFirestoreSubscription` mount 時呼叫。
 */
export function register(unsub: Unsubscribe): void {
  listeners.add(unsub);
}

/**
 * 移除一個 `Unsubscribe` handle(自然 unmount 已 unsub 過,僅清 registry)。
 *
 * 注意:不會再呼一次 unsub();caller 必須在呼本 function 之前自己 invoke。
 */
export function unregister(unsub: Unsubscribe): void {
  listeners.delete(unsub);
}

/**
 * 強制 detach 全部 listener 並清空 registry。
 *
 * 用途:`onAuthLost` 流程第一步;logout flow 第二步(BE revoke 之後)。
 *
 * 失敗的 unsub() 不會中斷其他 listener detach;每個 unsub 失敗紀錄 console.warn
 * 但不 throw。
 */
export function detachAll(): void {
  for (const unsub of listeners) {
    try {
      unsub();
    } catch (err) {
      console.warn('[firebase] listener detach failed', err);
    }
  }
  listeners.clear();
}

/**
 * 目前 active listener 數量(test 用 / Sentry metric 用 / leak guard 用)。
 */
export function activeListenerCount(): number {
  return listeners.size;
}
